import { useEffect, useRef } from 'react'
import { matchPath, useLocation, useNavigate, useNavigation } from 'react-router-dom'
import { Flash, useFlash } from '../components/common/Toasts'
import { APP_AREA_ROUTES } from '../routes'

const NUMERIC_ID = /^\d+$/

/**
 * Whether a URL is a signed-in screen this bundle renders, so reaching it needs no document load.
 * Every id in a URL Rails sends to SpaController is numeric, obfuscated ones included, and the
 * paths that share a pattern with one — payments/webpay, payments/check_pending_payment, a
 * `.pdf` or `.xls` member — are controller actions, as is the `?pdf=true` record preview. The
 * public routes are left out: the landing gets another stylesheet bundle, and the signed-out
 * documents load recorders the signed-in ones do not.
 */
export function isSpaPath(url: URL): boolean {
  if (url.origin !== window.location.origin || url.searchParams.get('pdf') === 'true') {
    return false
  }
  return APP_AREA_ROUTES.some((route) => {
    const match = matchPath({ path: route.path, caseSensitive: true }, url.pathname)
    return match !== null && Object.values(match.params).every((value) => NUMERIC_ID.test(value ?? ''))
  })
}

/**
 * Navigates once a save or lookup finishes, unless the user has meanwhile left the screen that
 * started it, or is on the way to another one: the request outlives the page, and its redirect
 * would pull them back. The save's flash is shown on the screen it lands on, or where the user
 * is when it does not navigate.
 */
export function useNavigateTo(): (target: string, flash?: Flash) => void {
  const navigate = useNavigate()
  const { showFlash, showFlashAfterNavigation } = useFlash()
  const { key } = useLocation()
  const { state } = useNavigation()
  const currentKey = useRef(key)
  const isNavigating = useRef(state !== 'idle')
  const isMounted = useRef(true)

  useEffect(() => {
    currentKey.current = key
  }, [key])

  useEffect(() => {
    isNavigating.current = state !== 'idle'
  }, [state])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  return (target: string, flash: Flash = {}) => {
    if (!isMounted.current || isNavigating.current || currentKey.current !== key) {
      showFlash(flash)
      return
    }
    const url = new URL(target, window.location.origin)
    if (isSpaPath(url)) {
      showFlashAfterNavigation(flash)
      navigate(url.pathname + url.search + url.hash)
    } else {
      showFlash(flash)
      window.location.assign(target)
    }
  }
}
